import React from 'react'
import Antecedence from '../../taskcomponents/Antecedence.js'
import SideConditionRule from '../../taskcomponents/SideConditionRule.js'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEraser} from '@fortawesome/free-solid-svg-icons'
import {requestLogRestService} from "../../taskcomponents/Task";
import Consequence from "../../taskcomponents/Consequence";
import {USER_CLEAR} from "../../constants";

/**
 * Displays the corresponding deduction rule where items and production rules
 * can be dropped.
 */
class CfgTopDownPredict extends React.Component {
    initial_values = {
        antecedence: '',
        i: '',
        displayI: 'i',
        alpha: '',
        displayAlpha: 'α',
        lhs: '',
        displayLhs: 'A',
        rhs: '',
        displayRhs: 'γ',
        prodRule: '',
        canDropItem: 0,
        canDropRule: 0,
        displayItem: '',
        displayRule: '',
        eraseDisabled: true
    };

    constructor(props) {
        super(props);
        this.checkNewItem = this.checkNewItem.bind(this);
        this.clear = this.clear.bind(this);
        this.extractAdditionalItemValues = this.extractAdditionalItemValues.bind(this);
        this.extractAdditionalRuleValues = this.extractAdditionalRuleValues.bind(this);
        this.setDisplayItem = this.setDisplayItem.bind(this);
        this.state = this.initial_values;
    }

    /**
     * Set item as antecedence in state and extract different other values from
     * it to set them in state.
     */
    setAntecedence(item) {
        this.setState({
            antecedence: item,
            eraseDisabled: false
        });
        this.extractAdditionalItemValues(item);
        this.checkNewItem();
    }

    extractAdditionalItemValues(item) {
        const space = item.itemForm.indexOf(' ');
        const stackHeadEnd = space > 0 ? space : item.itemForm.lastIndexOf(',');
        const lhs = item.itemForm.substring(1, stackHeadEnd);
        const alpha = stackHeadEnd === item.itemForm.lastIndexOf(',') ? 'ε'
            : item.itemForm.substring(stackHeadEnd + 1, item.itemForm.lastIndexOf(','));
        const i = item.itemForm.substring(item.itemForm.lastIndexOf(',') + 1,
            item.itemForm.length - 1);
        this.setState({
            lhs: lhs,
            alpha: alpha,
            i: i,
            canDropItem: 0,
            canDropRule: 0,
            displayItem: ''
        });
    }

    extractAdditionalDisplayItemValues(item) {
        const space = item.itemForm.indexOf(' ');
        const stackHeadEnd = space > 0 ? space : item.itemForm.lastIndexOf(',');
        const lhs = item.itemForm.substring(1, stackHeadEnd);
        const alpha = stackHeadEnd === item.itemForm.lastIndexOf(',') ? 'ε'
            : item.itemForm.substring(stackHeadEnd + 1, item.itemForm.lastIndexOf(','));
        const i = item.itemForm.substring(item.itemForm.lastIndexOf(',') + 1,
            item.itemForm.length - 1);
        this.setState({
            displayLhs: lhs,
            displayAlpha: alpha,
            displayI: i,
            canDropItem: 0,
            canDropRule: 0,
            displayItem: ''
        });
    }

    /**
     * Set rule as production rule in state and extract different other values
     * from it to be set in state.
     */
    setProdRule(rule) {
        this.setState({prodRule: rule, eraseDisabled: false});
        this.extractAdditionalRuleValues(rule);
        this.checkNewItem();
    }

    extractAdditionalRuleValues(rule) {
        const ruleSplit = rule.rule.split('->');
        const lhs = ruleSplit[0].trim();
        let rhs = ruleSplit[1].trim();
        if (rhs === '') {
            rhs = 'ε';
        }
        this.setState({lhs: lhs, rhs: rhs});
    }

    extractAdditionalDisplayRuleValues(rule) {
        const ruleSplit = rule.rule.split('->');
        const lhs = ruleSplit[0].trim();
        const rhs = ruleSplit[1].trim();
        this.setState({displayLhs: lhs, displayRhs: rhs});
    }

    setDisplayRule(rule) {
        this.setState({displayRule: rule});
        if (rule === '') {
            if (this.state.prodRule === '') {
                if (this.state.antecedence === '') {
                    this.setState({displayLhs: 'A', displayRhs: 'γ'});
                } else {
                    this.setState({displayRhs: 'γ'});
                    this.extractAdditionalDisplayItemValues(this.state.antecedence);
                }
            } else {
                this.extractAdditionalDisplayRuleValues(this.state.prodRule);
            }
        } else {
            this.extractAdditionalDisplayRuleValues(rule);
        }
    }

    /**
     * Returns true if item can be dropped on the antecedence component.
     */
    canDropItem(item) {
        const space = item.itemForm.indexOf(' ');
        const stackHeadEnd = space > 0 ? space : item.itemForm.lastIndexOf(',');
        const lhs = item.itemForm.substring(1, stackHeadEnd);
        return this.props.isNonterminal(lhs) &&
            (this.state.prodRule === '' || lhs === this.state.lhs);
    }

    /**
     * Returns true if rule can be dropped on the side condition component.
     */
    canDropRule(rule) {
        if (this.state.antecedence === '') {
            return true;
        }
        const ruleSplit = rule.rule.split('->');
        const lhs = ruleSplit[0].trim();
        return lhs === this.state.lhs;
    }

    /**
     * Sets item as displayItem in state and extracts different values from it
     * to be set in state.
     */
    setDisplayItem(item) {
        this.setState({
            displayItem: item
        });
        if (item === '') {
            if (this.state.antecedence === '') {
                if (this.state.prodRule === '') {
                    this.setState({
                        displayI: 'i',
                        displayAlpha: 'α',
                        displayLhs: 'A'
                    });
                } else {
                    this.setState({displayAlpha: 'α'});
                    this.extractAdditionalDisplayRuleValues(this.state.prodRule);
                }
            } else {
                this.extractAdditionalDisplayItemValues(this.state.antecedence);
            }
        } else {
            this.extractAdditionalDisplayItemValues(item);
        }
    }

    /**
     * Sets the canDropItem value in the state. 0 - no coloring, 1 - positive
     * highlighting of some parts, -1 negative highlighting.
     */
    setCanDropItem(value) {
        this.setState({
            canDropItem: value
        });
    }

    /**
     * Sets the canDropItem value in the state. 0 - no coloring, 1 - positive
     * highlighting of some parts, -1 negative highlighting.
     */
    setCanDropRule(value) {
        this.setState({
            canDropRule: value
        });
    }

    /**
     * If both antecedence and production rule are specified a new item is
     * generated from them and send to the task to be added to the table.
     */
    checkNewItem() {
        if (this.state.antecedence !== '' && this.state.prodRule !== '') {
            let newItem = '[';
            if (this.state.rhs !== 'ε') {
                newItem = newItem + this.state.rhs;
            }
            if (this.state.alpha !== 'ε' && this.state.rhs !== 'ε') {
                newItem = newItem + ' ';
            }
            if (this.state.alpha !== 'ε') {
                newItem = newItem + this.state.alpha;
            }
            if (this.state.alpha === 'ε' && this.state.rhs === 'ε') {
                newItem = newItem + 'ε';
            }
            newItem = newItem + ',' + this.state.i + ']';
            if (this.props.sendNewItemToTask(newItem, 'predict '
                + this.state.prodRule.rule, [this.state.antecedence.id])) {
                this.clear();
            }
        }
    }

    /**
     * Resets the initial values and sends log request.
     */
    clearWithLog() {
        requestLogRestService(USER_CLEAR(this.props.userSession, 'cfg-topdown-predict'));
        this.clear();
    }

    /**
     * Resets the initial values of the state.
     */
    clear() {
        this.setState(this.initial_values);
    }

    render() {
        const alpha = this.state.displayAlpha;
        const i = this.state.displayI;
        const lhs = this.state.displayLhs;
        const rhs = this.state.displayRhs;
        const canDropItem = this.state.canDropItem;
        const canDropRule = this.state.canDropRule;
        let canDropObject = 0;
        canDropObject = canDropItem !== 0 ? canDropItem : canDropObject;
        canDropObject = canDropRule !== 0 ? canDropRule : canDropObject;
        const itemHovering = canDropItem === 0 ? 0 : 1;
        const ruleHovering = this.state.displayRule === '' ? 0 : 1;
        return <div className='deduction-rule'>
            <div className='rule-name'>
                <button id='ded-rule-erase' disabled={this.state.eraseDisabled}
                        onClick={this.clearWithLog.bind(this)}>
                    <FontAwesomeIcon className={"clickable-icon"}
                                     icon={faEraser}/>
                    <span
                        className={'accessibility-label'}>clear top-down predict</span>
                </button>
                Predict:
            </div>
            <div className='middle-rule-part'>
                <div
                    className='antecedence'>
                    <Antecedence antecedenceName='cfg-topdown-predict'
                                 elementLists={[[lhs, alpha], [i]]}
                                 markings={[[canDropObject, canDropItem], [itemHovering]]}
                                 setAntecedence={this.setAntecedence.bind(this)}
                                 canDropItem={this.canDropItem.bind(this)}
                                 setDisplayItem={this.setDisplayItem.bind(this)}
                                 setCanDropItem={this.setCanDropItem.bind(this)}
                                 userSession={this.props.userSession}
                                 maxItem={this.props.maxItem}/>
                </div>
                <hr/>
                <div
                    className='consequence'>
                    <Consequence
                        elementLists={[[rhs, alpha], [i]]}
                        markings={[[ruleHovering, canDropItem], [itemHovering]]}
                        maxItem={this.props.maxItem}/>
                </div>
            </div>
            <div className='side-condition'>
                <SideConditionRule
                    displayLhs={lhs} displayRhs={rhs}
                    setProdRule={this.setProdRule.bind(this)}
                    setDisplayRule={this.setDisplayRule.bind(this)}
                    canDropRule={this.canDropRule.bind(this)}
                    setCanDropRule={this.setCanDropRule.bind(this)}
                    canDropLhs={canDropItem}
                    canDropRhs={ruleHovering}
                    userSession={this.props.userSession}
                    ruleName='cfg-topdown-predict'/> <span
                className={'unbreakable'}>∈ P</span>
            </div>
        </div>
    }
}

export default CfgTopDownPredict;
